/**
 * Grocery List Sync Middleware
 *
 * Automatically syncs grocery list changes to Firestore:
 * - Item checked/unchecked status
 * - Custom items added/removed
 * - List regeneration
 */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  toggleItem,
  clearPurchased,
  addCustomItem,
  removeCustomItem,
  setGeneratedList,
  clearList,
  moveToInventory,
  addIngredientsFromRecipe,
} from './groceryListSlice';
import { syncGroceryListToCloud } from '../services/groceryListSyncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import type { RootState } from './store';

// Create the listener middleware
export const groceryListSyncMiddleware = createListenerMiddleware();

// Debounce timer
let syncDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 1500; // 1.5 seconds

/**
 * Debounced sync to Firestore
 */
const debouncedSync = (userId: string, getState: () => unknown) => {
  if (syncDebounceTimer) {
    clearTimeout(syncDebounceTimer);
  }

  syncDebounceTimer = setTimeout(async () => {
    syncDebounceTimer = null;

    const state = getState() as RootState;
    const { items, customItems, generatedDate, weekSnapshot, sourceWeekStart, coversUntil } = state.groceryList;
    const householdId = state.auth?.householdId ?? null;

    try {
      await syncGroceryListToCloud(
        userId,
        items,
        customItems,
        generatedDate,
        weekSnapshot,
        sourceWeekStart ?? null,
        householdId,
        coversUntil ?? null
      );
      console.log(`🔄 Auto-synced grocery list to cloud (scope: ${householdId ? 'household' : 'user'})`);
    } catch (error) {
      console.error('Failed to auto-sync grocery list:', error);
    }
  }, DEBOUNCE_MS);
};

// Listen for grocery list mutations that should trigger sync
groceryListSyncMiddleware.startListening({
  matcher: isAnyOf(
    toggleItem,
    clearPurchased,
    addCustomItem,
    removeCustomItem,
    setGeneratedList,
    clearList,
    moveToInventory,
    addIngredientsFromRecipe
  ),
  effect: async (action, listenerApi) => {
    if (!isFirebaseEnabled()) {
      return;
    }

    const state = listenerApi.getState() as RootState;
    const userId = state.auth?.user?.uid;

    if (!userId) {
      return; // Not logged in
    }

    // Trigger debounced sync
    debouncedSync(userId, listenerApi.getState);
  },
});

export default groceryListSyncMiddleware;
